import { CircleAlertIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { parseError } from "@/api/errors";
import { cn } from "@/lib/utils";

type Props = {
  /** Anything a `catch` received, a message, or null for nothing to show. */
  error: unknown;
  /** Shown when the error carries no message of its own from the server. */
  fallback?: string;
  className?: string;
};

/**
 * A failure that belongs to a whole form or dialog -- the save, the create,
 * the delete. Field-level problems go under the field (`FieldError`), and
 * failures with no form to sit in go to a toast (`notifyError`).
 */
export default function FormAlert({ error, fallback, className }: Props) {
  if (error == null || error === "") return null;
  const parsed =
    typeof error === "string"
      ? { message: error, problems: undefined }
      : parseError(error, fallback);
  const problems =
    parsed.problems && parsed.problems.length > 1 ? parsed.problems : null;

  return (
    <Alert
      variant="destructive"
      className={cn("border-destructive/40 bg-destructive/5", className)}
    >
      <CircleAlertIcon />
      <AlertTitle className="line-clamp-none">{parsed.message}</AlertTitle>
      {problems && (
        <AlertDescription>
          <ul className="list-disc space-y-0.5 pl-4">
            {problems.map((problem, i) => (
              <li key={i}>{problem}</li>
            ))}
          </ul>
        </AlertDescription>
      )}
    </Alert>
  );
}
